import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTRPC } from '@/trpc/react'
import { arktypeResolver } from '@hookform/resolvers/arktype'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ColumnFiltersState } from '@tanstack/react-table'
import { type } from 'arktype'
import { Info } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { PayrollsDataTable } from './(payroll_table)/-components/PayrollsDataTable'
import { payrollTableColumns } from './(payroll_table)/-components/payrollTableColumns'

export const Route = createFileRoute('/dashboard/admin/payroll/')({
  component: RouteComponent,
  validateSearch: type({
    page: 'number = 1',
    pageSize: 'number = 50',
    status: 'string = ""',
    client: 'string = ""',
    frozen: 'string = ""',
  }),
})

const payrollFormSchema = type({
  clientId: 'string',
  payPeriodStart: 'string',
  payPeriodEnd: 'string',
  monthlySalaryCutoffMultiplier: 'string',
  governmentContributionMultiplier: 'string',
  // Stand-in in Step 10 (rendered/validated but not sent); wired into the real createPayroll
  // call below as of Step 16.
  withholdingTaxMultiplier: 'string',
})

type PayrollFormValues = typeof payrollFormSchema.infer
type ColumnFiltersUpdater = ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)

const payrollStatusValues = ['draft', 'approved', 'completed', 'error', 'archived'] as const
type PayrollStatusValue = (typeof payrollStatusValues)[number]

function getColumnFilterStringValues(
  columnFilters: ColumnFiltersState,
  columnId: string,
): string[] | undefined {
  const columnFilterValue = columnFilters.find(
    (columnFilter) => columnFilter.id === columnId,
  )?.value
  if (!Array.isArray(columnFilterValue)) {
    return undefined
  }

  const stringValues = columnFilterValue.filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  )

  return stringValues.length > 0 ? stringValues : undefined
}

function normalizePayrollStatusFilters(
  values: string[] | undefined,
): PayrollStatusValue[] | undefined {
  if (!values) {
    return undefined
  }

  const normalizedValues = values
    .map((value) => value.toLowerCase())
    .filter((value): value is PayrollStatusValue =>
      payrollStatusValues.includes(value as PayrollStatusValue),
    )

  return normalizedValues.length > 0 ? normalizedValues : undefined
}

function normalizeClientFilters(values: string[] | undefined): string[] | undefined {
  if (!values) {
    return undefined
  }

  const normalizedValues = values.filter((value) => value.trim().length > 0)
  return normalizedValues.length > 0 ? normalizedValues : undefined
}

function normalizeFrozenFilters(values: string[] | undefined): boolean[] | undefined {
  if (!values) {
    return undefined
  }

  const normalizedValues = values.flatMap((value) => {
    if (value === 'true') {
      return [true]
    }
    if (value === 'false') {
      return [false]
    }
    return []
  })

  const uniqueNormalizedValues = Array.from(new Set(normalizedValues))
  return uniqueNormalizedValues.length > 0 ? uniqueNormalizedValues : undefined
}

type PayrollSearch = {
  page: number
  pageSize: number
  status: string
  client: string
  frozen: string
}

function searchToColumnFilters(search: PayrollSearch): ColumnFiltersState {
  const filters: ColumnFiltersState = []
  const statusValues = normalizePayrollStatusFilters(
    search.status
      ? search.status
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined,
  )
  if (statusValues?.length) {
    filters.push({ id: 'status', value: statusValues })
  }
  const clientValues = normalizeClientFilters(
    search.client
      ? search.client
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined,
  )
  if (clientValues?.length) {
    filters.push({ id: 'client', value: clientValues })
  }
  const frozenRaw = search.frozen
    ? search.frozen
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s === 'true' || s === 'false')
    : []
  const frozenValues = frozenRaw.length > 0 ? Array.from(new Set(frozenRaw)) : undefined
  if (frozenValues?.length) {
    filters.push({ id: 'isFrozen', value: frozenValues })
  }
  return filters
}

function columnFiltersToSearchParams(filters: ColumnFiltersState): Partial<PayrollSearch> {
  const status = getColumnFilterStringValues(filters, 'status')
  const client = getColumnFilterStringValues(filters, 'client')
  const frozen = getColumnFilterStringValues(filters, 'isFrozen')
  return {
    ...(status?.length ? { status: status.join(',') } : { status: '' }),
    ...(client?.length ? { client: client.join(',') } : { client: '' }),
    ...(frozen?.length ? { frozen: frozen.join(',') } : { frozen: '' }),
  }
}

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const columnFilters = searchToColumnFilters(search)

  const page = Math.max(1, Math.trunc(search.page))
  const pageSize = Math.min(Math.max(1, Math.trunc(search.pageSize)), 200)
  const statusFilters = normalizePayrollStatusFilters(
    getColumnFilterStringValues(columnFilters, 'status'),
  )
  const clientFilters = normalizeClientFilters(
    getColumnFilterStringValues(columnFilters, 'client'),
  )
  const frozenFilters = normalizeFrozenFilters(
    getColumnFilterStringValues(columnFilters, 'isFrozen'),
  )

  const { data: clients } = useQuery({
    ...api.client.getClientsForBusiness.queryOptions(),
  })
  const getPayrollsInput = {
    page,
    pageSize,
    ...(statusFilters !== undefined && { statuses: statusFilters }),
    ...(clientFilters !== undefined && { clientIds: clientFilters }),
    ...(frozenFilters !== undefined && { isFrozen: frozenFilters }),
  }
  const { data: payrollsResult, isPending: isPayrollsPending } = useQuery({
    ...api.payroll.getPayrollsBusiness.queryOptions(getPayrollsInput),
  })
  const { mutate: createPayroll } = useMutation({
    ...api.payroll.createPayroll.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: api.payroll.getPayrollsBusiness.queryKey(),
        })
        toast.success('Payroll created')
      },
      onError: () => {
        toast.error('Failed to create payroll')
      },
    }),
  })

  const [dialogOpen, setDialogOpen] = useState(false)
  const form = useForm<PayrollFormValues>({
    resolver: arktypeResolver(payrollFormSchema),
    defaultValues: {
      clientId: '',
      payPeriodStart: '',
      payPeriodEnd: '',
      monthlySalaryCutoffMultiplier: '0.5',
      governmentContributionMultiplier: '0.5',
      withholdingTaxMultiplier: '1.00',
    },
  })

  const payPeriodStart = form.watch('payPeriodStart')
  const payPeriodEnd = form.watch('payPeriodEnd')

  function onSubmit(values: PayrollFormValues) {
    // Validate dates using Luxon
    const startDate = DateTime.fromISO(values.payPeriodStart)
    const endDate = DateTime.fromISO(values.payPeriodEnd)

    if (!startDate.isValid) {
      toast.error('Invalid pay period start date')
      return
    }

    if (!endDate.isValid) {
      toast.error('Invalid pay period end date')
      return
    }

    if (endDate <= startDate) {
      toast.error('Pay period end must be after start date')
      return
    }

    const daysDiff = endDate.diff(startDate, 'days').days
    if (daysDiff < 1) {
      toast.error('Pay period must be at least 1 day')
      return
    }

    if (daysDiff > 120) {
      toast.error('Pay period cannot exceed 120 days')
      return
    }

    createPayroll({
      clientId: values.clientId,
      payPeriodStart: values.payPeriodStart,
      payPeriodEnd: values.payPeriodEnd,
      monthlySalaryCutoffMultiplier: values.monthlySalaryCutoffMultiplier,
      governmentContributionMultiplier: values.governmentContributionMultiplier,
      withholdingTaxMultiplier: values.withholdingTaxMultiplier,
    })
    setDialogOpen(false)
    form.reset()
  }

  function handlePaginationChange(nextPagination: { page: number; pageSize: number }): void {
    const normalizedPageSize = Math.min(Math.max(1, Math.trunc(nextPagination.pageSize)), 200)
    const normalizedPage =
      normalizedPageSize !== pageSize ? 1 : Math.max(1, Math.trunc(nextPagination.page))

    navigate({
      search: (previousSearch) => ({
        ...previousSearch,
        page: normalizedPage,
        pageSize: normalizedPageSize,
      }),
    })
  }

  function handleColumnFiltersChange(nextColumnFiltersOrUpdater: ColumnFiltersUpdater): void {
    const nextColumnFilters =
      typeof nextColumnFiltersOrUpdater === 'function'
        ? nextColumnFiltersOrUpdater(columnFilters)
        : nextColumnFiltersOrUpdater

    navigate({
      search: (previousSearch) => ({
        ...previousSearch,
        page: 1,
        ...columnFiltersToSearchParams(nextColumnFilters),
      }),
    })
  }

  return (
    <DashboardShell>
      <div className="flex items-center justify-between mb-4">
        <DashboardHeader heading="Payroll" text="Generate payroll for your employees" />

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="default">Add Payroll</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg w-full">
            <DialogHeader>
              <DialogTitle>Add Payroll</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={!clients || clients.length === 0}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue
                              placeholder={
                                !clients || clients.length === 0
                                  ? 'No clients available'
                                  : 'Select client'
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.isArray(clients) && clients.length > 0 ? (
                              clients.map((client: { clientId: string; name: string }) => (
                                <SelectItem key={client.clientId} value={client.clientId}>
                                  {client.name}
                                </SelectItem>
                              ))
                            ) : (
                              <div className="px-4 py-2 text-muted-foreground text-sm">
                                No clients found
                              </div>
                            )}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex flex-col sm:flex-row gap-4">
                  <FormField
                    control={form.control}
                    name="payPeriodStart"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Pay Period Start</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            min="2015-01-01"
                            max="2035-12-31"
                            onChange={(e) => {
                              field.onChange(e)
                              // Trigger validation on payPeriodEnd when start changes
                              if (payPeriodEnd) {
                                form.trigger('payPeriodEnd')
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="payPeriodEnd"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Pay Period End</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            min="2015-01-01"
                            max="2035-12-31"
                            onChange={(e) => {
                              field.onChange(e)
                              // Trigger validation on payPeriodStart when end changes
                              if (payPeriodStart) {
                                form.trigger('payPeriodStart')
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="monthlySalaryCutoffMultiplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Salary Cutoff Multiplier</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select cutoff period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.5">Semi-monthly (0.5)</SelectItem>
                            <SelectItem value="1.0">Monthly (1.0)</SelectItem>
                            <SelectItem value="0.25">Weekly (0.25)</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="governmentContributionMultiplier"
                  render={({ field }) => {
                    // Parse the value, ensuring 0 is treated as a valid value (not falsy)
                    // Check if value exists and is not empty string, then parse it
                    // Only default to 0.5 if value is null, undefined, empty string, or NaN
                    // This prevents 0 from being incorrectly treated as falsy
                    let displayValue = 0.5
                    if (field.value != null && field.value !== '') {
                      const parsed = parseFloat(String(field.value))
                      if (!Number.isNaN(parsed)) {
                        displayValue = parsed
                      }
                    }

                    return (
                      <FormItem>
                        <FormLabel>Government Contribution Multiplier</FormLabel>
                        <FormControl>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                              <span>0.0</span>
                              <span className="font-medium">
                                {(displayValue * 100).toFixed(0)}%
                              </span>
                              <span>1.0</span>
                            </div>
                            <input
                              type="range"
                              min={0}
                              max={1}
                              step={0.01}
                              value={displayValue}
                              onChange={(e) => {
                                field.onChange(e.target.value)
                              }}
                              className="w-full"
                            />
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={1}
                                step={0.01}
                                value={field.value ?? '0.5'}
                                onChange={(e) => {
                                  const val = e.target.value
                                  if (
                                    val === '' ||
                                    (parseFloat(val) >= 0 && parseFloat(val) <= 1)
                                  ) {
                                    field.onChange(val === '' ? '0.5' : val)
                                  }
                                }}
                                className="w-24"
                              />
                              <span className="text-sm text-muted-foreground">
                                ({displayValue * 100}% of full contributions)
                              </span>
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )
                  }}
                />
                <FormField
                  control={form.control}
                  name="withholdingTaxMultiplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Withholding Tax Multiplier</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          value={field.value ?? '1.00'}
                          onChange={(e) => field.onChange(e.target.value)}
                          placeholder="1.00"
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Adjusts withheld tax from the legally-computed amount. Only change this
                        from 1.00 for a specific, documented reason (e.g. a year-end adjustment) —
                        withholding tax is legally mandated, and this is not a discretionary
                        setting.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <DialogFooter>
                  <Button type="submit" className="w-full">
                    Create Payroll
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-md border border-red-200 dark:border-red-800">
          <Info className="h-4 w-4" />
          <span>Only approved timesheets will be counted in the computations</span>
        </div>
      </div>

      {isPayrollsPending ? (
        <PayrollsDataTable
          columns={payrollTableColumns}
          data={[]}
          clients={clients}
          isLoading
          pagination={{
            page,
            pageSize,
            totalCount: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          }}
          onPaginationChange={handlePaginationChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
        />
      ) : Array.isArray(payrollsResult?.data) && payrollsResult.data.length > 0 ? (
        <PayrollsDataTable
          columns={payrollTableColumns}
          data={payrollsResult.data}
          clients={clients}
          pagination={payrollsResult.pagination}
          onPaginationChange={handlePaginationChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
        />
      ) : (
        <div className="text-center py-8">No payrolls found</div>
      )}
    </DashboardShell>
  )
}
